/**
 * Kiosk & Tablet Floor Helpers
 * Provides WakeLock (screen keep-awake) and Fullscreen controls for floor iPads.
 */

type WakeSentinel = { release(): Promise<void>; addEventListener(type: "release", listener: () => void): void };
let wakeLockSentinel: WakeSentinel | null = null;
let pendingWakeRequest: Promise<boolean> | null = null;
let wakeLockGeneration = 0;
let lastWakeRequestInvalidated = false;
const wakeLockListeners = new Set<() => void>();

function emitWakeLockState() { for (const listener of wakeLockListeners) listener(); }

export function subscribeWakeLock(listener: () => void): () => void {
  wakeLockListeners.add(listener);
  return () => wakeLockListeners.delete(listener);
}

export async function requestWakeLock(): Promise<boolean> {
  if (wakeLockSentinel) return true;
  if (pendingWakeRequest) return pendingWakeRequest;
  if (typeof navigator !== "undefined" && "wakeLock" in navigator) {
    lastWakeRequestInvalidated = false;
    const generation = wakeLockGeneration;
    pendingWakeRequest = (async () => {
    try {
      const sentinel = await (navigator as any).wakeLock.request("screen") as WakeSentinel;
      // Manual Off can happen while a permission prompt is still pending.
      if (generation !== wakeLockGeneration) {
        lastWakeRequestInvalidated = true;
        await sentinel.release().catch(() => {});
        return false;
      }
      wakeLockSentinel = sentinel;
      sentinel.addEventListener("release", () => {
        if (wakeLockSentinel !== sentinel) return;
        wakeLockSentinel = null;
        emitWakeLockState();
      });
      emitWakeLockState();
      return true;
    } catch (err) {
      console.warn("Screen WakeLock failed to acquire:", err);
      return false;
    } finally {
      pendingWakeRequest = null;
    }
    })();
    return pendingWakeRequest;
  }
  return false;
}

/** True only for the most recent request cancelled by a release race. */
export function wasWakeLockRequestInvalidated(): boolean { return lastWakeRequestInvalidated; }

export async function releaseWakeLock(): Promise<void> {
  wakeLockGeneration += 1;
  const sentinel = wakeLockSentinel;
  wakeLockSentinel = null;
  emitWakeLockState();
  if (sentinel) await sentinel.release().catch(() => {});
}

export function isWakeLockActive(): boolean {
  return wakeLockSentinel !== null;
}

export function isFullscreen(): boolean {
  if (typeof document === "undefined") return false;
  return Boolean(
    document.fullscreenElement ||
      (document as any).webkitFullscreenElement ||
      (document as any).mozFullScreenElement ||
      (document as any).msFullscreenElement
  );
}

export function toggleFullScreen(): boolean {
  if (typeof document === "undefined") return false;

  const docEl = document.documentElement as any;
  if (!isFullscreen()) {
    if (docEl.requestFullscreen) {
      docEl.requestFullscreen().catch(() => {});
    } else if (docEl.webkitRequestFullscreen) {
      docEl.webkitRequestFullscreen();
    } else if (docEl.msRequestFullscreen) {
      docEl.msRequestFullscreen();
    }
    return true;
  } else {
    if (document.exitFullscreen) {
      document.exitFullscreen().catch(() => {});
    } else if ((document as any).webkitExitFullscreen) {
      (document as any).webkitExitFullscreen();
    } else if ((document as any).msExitFullscreen) {
      (document as any).msExitFullscreen();
    }
    return false;
  }
}

export function isStandalonePWA(): boolean {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (window.navigator as any).standalone === true
  );
}
